import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Bug, Headset, Inbox, Lightbulb } from 'lucide-react';

import { ListRowsSkeleton } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import { dfLocale } from '@/lib/date-fns-locale';
import { BUG_REPORT_STATUS_META, BUG_REPORT_TYPE_META } from '../constants';
import { useMyBugReports } from '../hooks/use-bug-reports';
import type { IBugReport } from '../types';
import { ReportConversation } from './report-conversation';

// Support Request uses the headset, matching the picker on the New Report tab —
// the same report type must not wear two different glyphs one tab apart. See
// the note on `TYPE_ICONS` in `report-bug-form.tsx`.
const TYPE_ICONS = { bug: Bug, lightbulb: Lightbulb, lifeBuoy: Headset } as const;

function ReportRow({ report }: { readonly report: IBugReport }) {
    const { t } = useTranslation();
    const typeMeta = BUG_REPORT_TYPE_META[report.type];
    const statusMeta = BUG_REPORT_STATUS_META[report.status];
    const Icon = TYPE_ICONS[typeMeta.icon];

    return (
        <AccordionItem value={report.id}>
            <AccordionTrigger className="gap-3 px-4 py-3">
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="flex min-w-0 items-center gap-2">
                        <Icon className="size-3.5 shrink-0 text-text-tertiary" />
                        <span className="min-w-0 truncate text-sm font-medium text-text-primary">
                            {report.title}
                        </span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                        <Badge variant={statusMeta.variant}>
                            {t(statusMeta.labelKey, statusMeta.label)}
                        </Badge>
                        <span className="text-xs font-normal text-text-tertiary">
                            {format(new Date(report.createdAt), 'PP', { locale: dfLocale() })}
                        </span>
                    </span>
                </span>
            </AccordionTrigger>

            {/* Radix unmounts closed content, so the thread request only fires
                for the report the reporter actually opened. */}
            <AccordionContent className="px-4 pt-0 pb-4">
                <ReportConversation
                    reportId={report.id}
                    description={report.description}
                    createdAt={report.createdAt}
                />
            </AccordionContent>
        </AccordionItem>
    );
}

interface IMyReportsListProps {
    readonly onFileNew: () => void;
    /**
     * Report to expand on mount — set when the user arrived by clicking the
     * notification about it, so the reply they were told about is on screen
     * rather than one click behind a collapsed row.
     */
    readonly openReportId?: string | undefined;
}

/**
 * Everything this user has filed, each row expanding into its conversation
 * with the support team. Scoped by the API to the caller — an org admin does
 * not see a colleague's reports here.
 */
export function MyReportsList({ onFileNew, openReportId }: IMyReportsListProps) {
    const { t } = useTranslation();
    const { data, isLoading, isError, refetch } = useMyBugReports();

    if (isLoading) {
        return <ListRowsSkeleton rows={3} />;
    }

    if (isError) {
        return (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
                <p className="text-sm text-text-secondary">
                    {t('bugReports.myReports.error', 'Failed to load your reports.')}
                </p>
                <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
                    {t('common.retry', 'Retry')}
                </Button>
            </div>
        );
    }

    const reports = data?.data ?? [];

    if (reports.length === 0) {
        return (
            <Empty className="py-8">
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                        <Inbox />
                    </EmptyMedia>
                    <EmptyTitle>
                        {t('bugReports.myReports.emptyTitle', 'Nothing reported yet')}
                    </EmptyTitle>
                    <EmptyDescription>
                        {t(
                            'bugReports.myReports.emptyDescription',
                            'Reports you send us show up here, along with our replies.',
                        )}
                    </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <Button type="button" variant="outline" size="sm" onClick={onFileNew}>
                        {t('bugReports.myReports.fileNew', 'New report')}
                    </Button>
                </EmptyContent>
            </Empty>
        );
    }

    return (
        // Uncontrolled with a default: the dialog is remounted on every open
        // (see ReportBugTrigger's session key), so the deep-linked row opens
        // each time without the accordion fighting the user's own clicks.
        <Accordion type="single" collapsible defaultValue={openReportId}>
            {reports.map((report) => (
                <ReportRow key={report.id} report={report} />
            ))}
        </Accordion>
    );
}
